import { definePage, type PluginWebManifest } from '@reflex/plugin-api/web';

const overview = definePage({
  path: '/configure/usage/model-costs',
  label: 'Model Costs',
  icon: 'DollarSign',
  group: 'organization',
  componentPath: '@reflex/plugin-costs/web/CostsOverviewPage',
  description: 'Track LLM spend across users, provider keys, and models.',
  details:
    'Records every agent turn (and accepts manual ingestion) so org admins can see total spend, drill into a specific user, or audit a single provider key. Rates are snapshotted at write time, so historical totals stay stable when published pricing changes.',
  features: [
    'Per-user spend breakdown with drill-down',
    'Per-provider-key spend breakdown with drill-down',
    'Per-model spend breakdown',
    'Per-label spend breakdown — what kinds of work the budget goes to',
    'Self-serve "Your usage" card on every user profile',
    'CSV export on every table',
    'Idempotent ingestion from agent lifecycle events + manual API',
  ],
});

export const costsWeb: PluginWebManifest = {
  ...overview.web,
  routes: [
    ...overview.web.routes!,
    {
      path: '/configure/usage/model-costs/users/:userId',
      componentPath: '@reflex/plugin-costs/web/CostsUserDetailPage',
    },
    {
      path: '/configure/usage/model-costs/keys/:secretId',
      componentPath: '@reflex/plugin-costs/web/CostsKeyDetailPage',
    },
    // Full paginated + sortable records for a key ("view all"), and the
    // same page scoped to one category (the by-category drill-in).
    {
      path: '/configure/usage/model-costs/keys/:secretId/records',
      componentPath: '@reflex/plugin-costs/web/CostsKeyRecordsPage',
    },
    {
      path: '/configure/usage/model-costs/keys/:secretId/records/:category',
      componentPath: '@reflex/plugin-costs/web/CostsKeyRecordsPage',
    },
    {
      path: '/configure/usage/model-costs/providers/:provider',
      componentPath: '@reflex/plugin-costs/web/CostsProviderDetailPage',
    },
    {
      path: '/configure/usage/model-costs/categories/:category',
      componentPath: '@reflex/plugin-costs/web/CostsCategoryDetailPage',
    },
    // `:labelId` is an `alb_*` agent-label id, or `__untagged__` for the
    // residual — see `UNTAGGED_SERIES_KEY`.
    {
      path: '/configure/usage/model-costs/labels/:labelId',
      componentPath: '@reflex/plugin-costs/web/CostsLabelDetailPage',
    },
  ],
  // Contributed to the user's own profile page so every user can see
  // their own spend without needing the org-admin Costs view.
  profileSections: [
    {
      id: 'costs-spend-heatmap',
      label: 'Agent spend',
      icon: 'TrendingUp',
      componentPath: '@reflex/plugin-costs/web/SpendHeatmapSection',
      order: 15,
    },
    {
      id: 'costs-my-usage',
      label: 'Your usage',
      icon: 'DollarSign',
      componentPath: '@reflex/plugin-costs/web/MyCostsSection',
      order: 20,
    },
  ],
  // `cost-keys` is a costs-owned preview kind so any ResourceChip
  // referencing a `mps_*` id from this plugin renders a small popover
  // with the key's provider/scope/spend and a link into the existing
  // key-detail page. We intentionally do not register the broader
  // `model-provider-secrets` kind — the host's credentials surface
  // owns that namespace.
  resourcePreviews: [
    {
      kind: 'cost-keys',
      label: 'Provider key',
      icon: 'KeyRound',
      componentPath: '@reflex/plugin-costs/web/CostsKeyPreview',
      detailHref: '/configure/usage/model-costs/keys/:id',
      // Claim the `mps_*` prefix so a bare model-provider-secret id in agent
      // output badges to this same key popover (provider/scope/spend) via the
      // entity-id registry, matching the explicit `<ResourceChip kind="cost-keys">`.
      idPrefixes: ['mps'],
    },
  ],
  marketingPreviews: [
    {
      id: 'costs-overview-page',
      label: 'Costs overview page',
      description:
        'Sample LLM spend dashboard with KPIs, trend chart, and a category/user/key/model breakdown.',
      surface: 'page',
      componentPath: '@reflex/plugin-costs/web/CostsOverviewPagePreview',
    },
  ],
};
